'use client';

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import gsap from 'gsap';
import { Icon, Link, Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* Helpers
import { config } from '@/helpers';
import headerLogoSVG from './utils';

//* Styles
import HeaderStyle from './style';

//* Components
import BurgerMenu from './BurgerMenu';
import { withDataContext } from '@/context';

const instagramUsername = 'avagyanwedding';

const Header = forwardRef(({ firstLoad, setFirstLoad, isPreloaderActive, isMobileScreen, showPreloader, isPortfolioIndividual, cleanedPathname }, ref) => {
	const { winWidth, isBurgerMenuOpen, toggleBurgerMenu, closeBurgerMenu } = useUi();
	const { selectedLang } = useTranslation();
	const pathname = usePathname();

	//! Refs
	const headerRef = useRef(null);
	const oldScroll = useRef(0);

	//! State
	const [show, setShow] = useState(true);

	//! Close burger menu on window width or route/language change
	useEffect(() => {
		closeBurgerMenu();
	}, [winWidth, pathname, selectedLang]);

	//! Manage body scroll based on burger menu and screen size
	useEffect(() => {
		if (isMobileScreen && isBurgerMenuOpen) {
			document.body.classList.add('hidden-wrapper');
		} else {
			document.body.classList.remove('hidden-wrapper');
		}

		return () => {
			document.body.classList.remove('hidden-wrapper');
		};
	}, [isBurgerMenuOpen, isMobileScreen]);

	//! Scroll behavior to hide/show header
	const onScroll = useCallback(() => {
		const currentScroll = window.scrollY;

		if (currentScroll > oldScroll.current && currentScroll > 0 && !isBurgerMenuOpen) {
			setShow(false);
		} else {
			setShow(true);
		}

		oldScroll.current = currentScroll;
	}, [isBurgerMenuOpen]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, [onScroll]);

	useEffect(() => {
		onScroll(); //! Initial call to apply scroll behavior on mount or route/lang change
	}, [pathname, selectedLang, onScroll]);

	//! Animate header visibility with GSAP

	useEffect(() => {
		if (!isPreloaderActive && headerRef.current) {
			gsap.killTweensOf(headerRef.current);
			const getProperty = gsap.getProperty(headerRef.current, 'y');

			if (firstLoad) {
				setFirstLoad();
			}

			if ((!getProperty && firstLoad.current) || window.scrollY !== 0) {
				gsap.to(headerRef.current, {
					duration: window.scrollY === 0 ? 0.7 : 0.3,
					delay: showPreloader && window.scrollY === 0 ? 0.8 : 0.1,
					y: isPortfolioIndividual && !isMobileScreen ? '0%' : show ? '0%' : '-100%',
				});
			}
		}
	}, [show, isPreloaderActive, showPreloader, isPortfolioIndividual, isMobileScreen]);

	if (isPreloaderActive) return null;

	return (
		<>
			<HeaderStyle
				ref={headerRef}
				className={`header-container ${isBurgerMenuOpen ? 'burger-active' : ''} ${firstLoad ? 'translate' : ''}`}>
				<div className='header-inner-wrapper'>
					<div
						onClick={toggleBurgerMenu}
						className='btn-hover-relative-block cursor-pointer'>
						<Text
							text='menu'
							className='p5 font-neue-roman white-1000 uppercase menu-close-text anim-state1'
						/>
						<Text
							text='menu'
							className='p5 font-neue-roman white-1000 uppercase menu-close-text anim-state2'
						/>
					</div>

					<div className='header-svg-block'>
						<Link
							className='logo-wrapper'
							url={config.routes.home.path}
							onClick={() => isBurgerMenuOpen && closeBurgerMenu()}>
							{headerLogoSVG}
						</Link>
					</div>

					<div className='right-block'>
						<Link
							external
							url={`https://www.instagram.com/${instagramUsername}`}>
							{isMobileScreen ? (
								<Icon
									name='instagram'
									className='instagram-icon'
								/>
							) : (
								<Text
									text={`@${instagramUsername}`}
									className='p5 font-neue-roman white-1000 instagram-username-text'
								/>
							)}
						</Link>
					</div>
				</div>
			</HeaderStyle>

			<BurgerMenu
				ref={ref}
				cleanedPathname={cleanedPathname}
				isMobileScreen={isMobileScreen}
			/>
		</>
	);
});

export default withDataContext(Header, ['firstLoad', 'setFirstLoad']);
